import {useContext, useMemo} from 'react';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {useGetBiobookPreferencesQuery, useGetBiobookQuery} from '../../../app/services/biobookApi';
import {useCheckBiotwinCalculationQuery, useGetBiotwinQuery} from '../../../app/services/biotwinApi';
import {isCompleteBiotwin, isNeoplastic} from '../../biotwin/helpers/utils';
import {PersonContext} from '../../person/contexts/PersonContext';
import {selectSelectedConditionIds, selectSelectedFilter} from '../../person/personSlice';
import {getDrugMultiIndexedBiobookPages, getMultiIndexedBiobookPages} from '../utils';

export const useBiobookPages = () => {
  const {hasRelation, relevantConditions, userId} = useContext(PersonContext);
  const {data: biobook = []} = useGetBiobookQuery({userId});
  const {data: biotwin} = useGetBiotwinQuery({id: userId});
  const {isLoading: isLoadingCheckBiotwinCalculation} = useCheckBiotwinCalculationQuery({id: userId});
  const selectedConditionIds = useAppSelector(selectSelectedConditionIds);
  const selectedFilter = useAppSelector(selectSelectedFilter);

  const {data: biobookPreferences} = useGetBiobookPreferencesQuery(
    {userId},
    {skip: isLoadingCheckBiotwinCalculation || !isCompleteBiotwin(biotwin)},
  );

  const favoritePhraseIds = biobookPreferences?.favoritePhraseIds;
  const biobookUpdatePhraseIds = biotwin?.biobookUpdatePhraseIds;

  const updatedBiobookPages = useMemo(
    () => biobook.filter((biobookPage) => biobookUpdatePhraseIds?.includes(biobookPage.phraseId)),
    [biobook, biobookUpdatePhraseIds],
  );
  const bookmarkedBiobookPages = useMemo(
    () => biobook.filter((biobookPage) => favoritePhraseIds?.includes(biobookPage.phraseId)),
    [biobook, favoritePhraseIds],
  );
  const multiIndexedBiobookPages = useMemo(
    () => (relevantConditions ? getMultiIndexedBiobookPages(biobook, relevantConditions, hasRelation) : []),
    [biobook, hasRelation, relevantConditions],
  );
  const drugMultiIndexedBiobookPages = useMemo(
    () => (relevantConditions ? getDrugMultiIndexedBiobookPages(biobook, relevantConditions, hasRelation) : []),
    [biobook, hasRelation, relevantConditions],
  );
  const standardAndPolicyBiobookPages = useMemo(
    () => biobook.filter((biobookPage) => biobookPage.isStandardAndPolicy),
    [biobook],
  );
  const neoplasticBiobookPages = useMemo(() => biobook.filter((biobookPage) => isNeoplastic(biobookPage)), [biobook]);

  const matchedBiobookPages = biobook.filter((biobookPage) => {
    if (selectedFilter === 'updates' && !updatedBiobookPages.includes(biobookPage)) {
      return false;
    }

    if (selectedFilter === 'favorites' && !bookmarkedBiobookPages.includes(biobookPage)) {
      return false;
    }

    if (selectedFilter === 'multiIndexed' && !multiIndexedBiobookPages.includes(biobookPage)) {
      return false;
    }

    if (selectedFilter === 'drugMultiIndexed' && !drugMultiIndexedBiobookPages.includes(biobookPage)) {
      return false;
    }

    if (selectedFilter === 'neoplastic' && !neoplasticBiobookPages.includes(biobookPage)) {
      return false;
    }

    if (selectedFilter === 'standardAndPolicy' && !standardAndPolicyBiobookPages.includes(biobookPage)) {
      return false;
    }

    return selectedConditionIds.every((conditionId) => biobookPage.matchedConditionIds.includes(conditionId));
  });

  return {
    biobook,
    matchedBiobookPages,
    multiIndexedBiobookPages,
    drugMultiIndexedBiobookPages,
    neoplasticBiobookPages,
    standardAndPolicyBiobookPages,
    bookmarkedBiobookPages,
    updatedBiobookPages,
  };
};
